import { createFileRoute } from '@tanstack/react-router';
import { ManagePaymentsError } from '@/features/manage-payments/components/manage-payments-error';
import { ManagePaymentsSkeleton } from '@/features/manage-payments/components/manage-payments-skeleton';
import { ManagePaymentsLayout } from '@/features/manage-payments/manage-payments-layout';
import { fetchManagePayments } from '@/features/manage-payments/services/fetch-manage-payments';
import { fetchPaymentStatus } from '@/features/manage-payments/services/fetch-payment-status';
import {
	logManagePaymentsLokiError,
	logManagePaymentsLokiErrorFromUnknown,
	ManagePaymentsLokiErrorType,
} from '@/lib/loki-logger';

interface ManagePaymentsSearch {
	transactionId?: string;
}

export const Route = createFileRoute('/_auth/ixi-finance/manage-payments')({
	validateSearch: (search: Record<string, unknown>): ManagePaymentsSearch => ({
		transactionId: search.transactionId as string | undefined,
	}),
	loaderDeps: ({ search }) => ({ transactionId: search.transactionId }),
	loader: async ({ deps }) => {
		let isCardSaved = false;

		if (deps.transactionId) {
			try {
				const status = await fetchPaymentStatus(deps.transactionId);
				isCardSaved = !!status.success;

				if (!status.success) {
					const detail =
						status.failure != null
							? JSON.stringify(status.failure)
							: status.pending != null
								? JSON.stringify(status.pending)
								: 'no_success_payload';
					logManagePaymentsLokiError(
						ManagePaymentsLokiErrorType.PAYMENT_CALLBACK_NOT_SUCCESS,
						{ detail },
					);
				}

				// Push the current path (without query params) on top so the user stays on manage-payments.
				const cleanPath = window.location.pathname;
				window.history.replaceState({}, '', cleanPath);
			} catch (err) {
				logManagePaymentsLokiErrorFromUnknown(
					ManagePaymentsLokiErrorType.ERROR_FETCH_PAYMENT_STATUS,
					err,
					{ transactionId: deps.transactionId },
				);
				// Continue with manage-payments fetch; isCardSaved stays false
			}
		}

		try {
			const payments = await fetchManagePayments();
			return { ...payments, isCardSaved };
		} catch (err) {
			logManagePaymentsLokiErrorFromUnknown(
				ManagePaymentsLokiErrorType.ERROR_FETCH_MANAGE_PAYMENT_OPTIONS,
				err,
			);
			throw err;
		}
	},
	component: ManagePaymentsLayout,
	pendingComponent: ManagePaymentsSkeleton,
	errorComponent: ManagePaymentsError,
	ssr: false,
	// 'stay' means the URL hash changed without a route transition — skip
	// loader re-runs so hash-based sheet state doesn't trigger refetches.
	shouldReload: ({ cause }) => cause !== 'stay',
});
